/**
 * AudioManager.js
 * A robust utility to handle audio notifications in WebView environments.
 * Manages audio objects in memory to avoid DOM-related issues and re-渲染 glitches.
 */

class AudioManager {
  constructor() {
    this.sounds = {};
    this.isUnlocked = false;
  }

  /**
   * Register sounds with their paths.
   * @param {Object} soundMap - Example: { noti_sound: './update.mp3' }
   */
  init(soundMap) {
    Object.entries(soundMap).forEach(([id, path]) => {
      try {
        const audio = new Audio(path);
        // Preload for better performance
        audio.preload = 'auto';
        this.sounds[id] = audio;
        console.log(`[AudioManager] Registered sound: ${id} -> ${path}`);
      } catch (err) {
        console.error(`[AudioManager] Failed to initialize sound ${id}:`, err);
      }
    });
  }

  /**
   * Programmatically "unlock" all registered sounds.
   * Should be called from a user interaction event (click/touchstart).
   */
  async unlock() {
    if (this.isUnlocked) return;

    console.log('[AudioManager] Attempting to unlock audio...');
    const unlockPromises = Object.values(this.sounds).map(async (audio) => {
      try {
        // Play and immediately pause to satisfy autoplay policies
        await audio.play();
        audio.pause();
        audio.currentTime = 0;
      } catch (err) {
        // Some browsers might still block this if not called exactly right,
        // but we swallow it as the best effort.
        console.warn('[AudioManager] Unlock attempt for a sound failed:', err.message);
      }
    });

    await Promise.all(unlockPromises);
    this.isUnlocked = true;
    console.log('[AudioManager] Audio system unlocked.');
  }

  /**
   * Play a registered sound by ID.
   * @param {string} id - The sound ID registered in init().
   */
  async play(id) {
    const audio = this.sounds[id];
    if (!audio) {
      console.error(`[AudioManager] Sound ID "${id}" not found.`);
      return;
    }

    try {
      // If already playing, reset to start
      if (!audio.paused) {
        audio.pause();
        audio.currentTime = 0;
      }

      await audio.play();
      console.log(`[AudioManager] Playing sound: ${id}`);
    } catch (err) {
      console.error(`[AudioManager] Playback failed for "${id}":`, err);
      
      // Fallback: If it's a "NotAllowedError", it might need another unlock.
      if (err.name === 'NotAllowedError') {
        console.warn('[AudioManager] Playback blocked by autoplay policy. Need user interaction.');
        this.isUnlocked = false; 
      }
    }
  }
}

const instance = new AudioManager();
export default instance;
